import {
  AppstoreAddOutlined,
  CheckOutlined,
  DiffOutlined,
} from "@ant-design/icons";
import { assertNever } from "@p0security/shared/types/index";
import { integrationLabels } from "@p0security/shared/types/workflow/constants";
import {
  Descriptions,
  Flex,
  Modal,
  Spin,
  type StepProps,
  Steps,
  Typography,
} from "antd";
import { get, startCase } from "lodash";
import { useMemo, useState } from "react";

import { ErrorDisplay } from "../../../../Error.tsx";
import { ACCESSES_MAP } from "../../../../Jit/Requests/requestUtils.ts";
import { useRequest } from "../hooks/useRequest.ts";

const StepIndices = ["review", "creating", "done"] as const;

const steps: StepProps[] = [
  { status: "process", title: "Reviewing", icon: <DiffOutlined /> },
  { status: "wait", title: "Creating", icon: <AppstoreAddOutlined /> },
  { status: "finish", title: "Created", icon: <CheckOutlined /> },
];

export const UpdatePolicyModal: React.FC<{
  action: "allow" | "block";
  agent: string;
  onClose: () => void;
  open: boolean;
  requestId: string;
}> = ({ action, agent, open, onClose, requestId }) => {
  const request = useRequest(requestId);
  const { doc } = request;
  const [step, _setStep] = useState<(typeof StepIndices)[number]>("review");

  const label = useMemo(() => startCase(action), [action]);

  const data = doc?.data;

  const access =
    data && ACCESSES_MAP[data.type as keyof typeof ACCESSES_MAP][data.access];
  const { extra, locator, policy } = access?.render ?? {};

  return (
    <Modal
      closable={false}
      maskClosable={false}
      okButtonProps={action === "block" ? { danger: true } : {}}
      okText={label}
      open={open}
      onCancel={step === "done" ? undefined : onClose}
    >
      {request.loading ? (
        <Spin />
      ) : data && access ? (
        <Flex vertical gap={24}>
          <Steps items={steps} current={StepIndices.indexOf(step)} />
          {step === "review" ? (
            <Flex vertical gap={12}>
              <div>
                Clicking &quot;{label}&quot; will create a new access-control
                policy that will{" "}
                <Typography.Text strong>{action}</Typography.Text> this access.
              </div>
              <Descriptions
                bordered
                layout="horizontal"
                column={1}
                size="small"
              >
                <Descriptions.Item label="Agent">{agent}</Descriptions.Item>
                <Descriptions.Item label="User">
                  {data.requestor}
                </Descriptions.Item>
                <Descriptions.Item label="Resource">
                  {get(integrationLabels, data.type) ?? "Unknown"}
                </Descriptions.Item>
                {locator && (
                  <Descriptions.Item label="Location">
                    {String(data.permission[locator])}
                  </Descriptions.Item>
                )}
                {policy && (
                  <Descriptions.Item label={access.label}>
                    {String(data.permission[policy])}
                  </Descriptions.Item>
                )}
                {(extra ?? []).map((key) => (
                  <Descriptions.Item
                    key={key}
                    label={access.parameters[key].label}
                  >
                    {String(data.permission[key])}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Flex>
          ) : step === "creating" ? (
            <></>
          ) : step === "done" ? (
            <></>
          ) : (
            assertNever(step)
          )}
        </Flex>
      ) : (
        <ErrorDisplay error="Request not found" />
      )}
    </Modal>
  );
};
